import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, Calendar } from "lucide-react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Reveal } from "../components/Motion";
import { api } from "../lib/api";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setPost(null);
    setNotFound(false);
    api.get(`/blogs/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div data-testid="blog-not-found" className="max-w-3xl mx-auto px-5 py-40 text-center">
        <h1 className="font-display text-4xl text-white">Story not found</h1>
        <Link to="/blog" className="mt-6 inline-flex items-center gap-2 text-[#ffd100] font-medium">
          <ArrowLeft size={18} /> Back to blog
        </Link>
      </div>
    );
  }

  if (!post) {
    return <div className="max-w-3xl mx-auto px-5 py-40 text-neutral-400 text-lg">Loading…</div>;
  }

  const date = new Date(post.published_at).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });
  const canonical = `https://www.shaancurryhouse.com/blog/${post.slug}`;
  const articleLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    image: [post.image],
    datePublished: post.published_at,
    dateModified: post.published_at,
    author: { "@type": "Organization", name: post.author || "Shaan Curry House" },
    publisher: {
      "@type": "Organization",
      name: "Shaan Curry House",
      logo: { "@type": "ImageObject", url: "https://www.shaancurryhouse.com/apple-touch-icon.png" },
    },
    description: post.excerpt,
    mainEntityOfPage: canonical,
    articleSection: post.category,
  };
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://www.shaancurryhouse.com/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://www.shaancurryhouse.com/blog" },
      { "@type": "ListItem", position: 3, name: post.title, item: canonical },
    ],
  };

  return (
    <article data-testid="blog-post-page">
      <Helmet prioritizeSeoTags>
        <title>{`${post.title} | Shaan Curry House Blog`}</title>
        <meta name="description" content={post.excerpt} />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" href={canonical} hrefLang="en-CA" />
        <link rel="alternate" href={canonical} hrefLang="x-default" />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Shaan Curry House" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={post.image} />
        <meta property="og:locale" content="en_CA" />
        <meta property="article:published_time" content={post.published_at} />
        <meta property="article:section" content={post.category} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        <meta name="twitter:image" content={post.image} />
        <script type="application/ld+json">{JSON.stringify(articleLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      </Helmet>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10">
        <motion.img
          initial={{ scale: 1.1 }} animate={{ scale: 1 }} transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          src={post.image} alt={post.title} className="absolute inset-0 w-full h-full object-cover" loading="eager" />
        <div className="absolute inset-0 bg-[#0a0a0a]/80" />
        <div className="relative z-10 max-w-3xl mx-auto px-5 md:px-10 pt-28 md:pt-36 pb-16 md:pb-20">
          <Reveal>
            <Link to="/blog" className="inline-flex items-center gap-2 text-[#ffd100] font-medium mb-6 hover:gap-3 transition-all">
              <ArrowLeft size={18} /> All Stories
            </Link>
            <span className="block text-[#ffd100] font-semibold uppercase tracking-[0.15em] text-sm mb-4">{post.category}</span>
            <h1 className="font-display font-bold text-white text-4xl md:text-6xl tracking-tight leading-[1.02]">{post.title}</h1>
            <div className="mt-6 flex flex-wrap items-center gap-5 text-neutral-300 text-base">
              <span className="flex items-center gap-2"><Calendar size={16} /> {date}</span>
              <span className="flex items-center gap-2"><Clock size={16} /> {post.read_time}</span>
              <span>By {post.author}</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* BODY */}
      <section className="max-w-3xl mx-auto px-5 md:px-10 py-16 md:py-20">
        <Reveal>
          <p className="prose-shaan text-2xl text-neutral-100 font-serif-accent italic leading-relaxed mb-10">{post.excerpt}</p>
          <div className="prose-shaan">
            {post.content.split("\n\n").map((block, i) =>
              block.startsWith("## ") ? (
                <h2 key={i}>{block.replace("## ", "")}</h2>
              ) : (
                <p key={i}>{block}</p>
              )
            )}
          </div>
        </Reveal>

        <div className="mt-14 pt-10 border-t border-white/10 text-center">
          <p className="text-neutral-300 text-lg">Hungry now? Let us cater your next celebration.</p>
          <Link to="/catering" data-testid="blog-catering-cta"
            className="mt-5 inline-flex items-center justify-center bg-[#ffd100] text-[#0a0a0a] font-bold text-lg px-8 py-4 rounded-full hover:bg-[#e5bc00] transition-colors">
            Explore Catering
          </Link>
        </div>
      </section>
    </article>
  );
}
